import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock, ArrowRight, Shield } from 'lucide-react';

const TutorialsPage: React.FC = () => {
  const tutorials = [
    {
      slug: 'why-backups-matter',
      title: 'Why Backups Matter: A Cautionary Tale',
      level: 'Beginner-friendly',
      readTime: 8,
      excerpt: "The Operator's thoughts on backup strategy, disaster recovery philosophy, and the 3-2-1 rule explained through datacenter parables.",
      available: true
    },
    {
      slug: 'defense-in-depth',
      title: 'Defense in Depth: The Onion Model (And Why It Makes You Cry)',
      level: 'Conceptual',
      readTime: 7,
      excerpt: 'Security isn\'t a wall. It\'s layers. Like an onion, or an ogre, or that organizational chart nobody understands.',
      available: false
    },
    {
      slug: 'least-privilege',
      title: 'The Philosophy of Least Privilege',
      level: 'Conceptual',
      readTime: 6,
      excerpt: 'Why giving users only what they need isn\'t just security — it\'s self-preservation.',
      available: false
    },
    {
      slug: 'network-segmentation',
      title: 'Network Segmentation for Humans',
      level: 'Conceptual',
      readTime: 7,
      excerpt: 'Dividing networks isn\'t just good practice. It\'s blast radius management. Learn the metaphors that matter.',
      available: false
    },
    {
      slug: 'incident-response',
      title: 'Incident Response Theatre',
      level: 'Intermediate',
      readTime: 10,
      excerpt: 'The theory behind handling security incidents: detection, containment, eradication, recovery. No specifics, all concepts.',
      available: false
    },
    {
      slug: 'least-surprise',
      title: 'The Principle of Least Surprise',
      level: 'Conceptual',
      readTime: 5,
      excerpt: 'Why predictable systems are secure systems, and how to design with failure in mind.',
      available: false
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">

          {/* Page Header */}
          <div className="mb-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-teal-500/10 border-2 border-teal-500 rounded-xl flex items-center justify-center">
                <BookOpen className="w-10 h-10 text-teal-500" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              Conceptual Tutorials
            </h1>
            <p className="text-xl text-gray-400 mb-6">
              High-Level Security & Sysadmin Theory
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-100 mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-teal-500" />
              Learn the Concepts (Not the Exploits)
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              These tutorials teach high-level security and sysadmin principles through conceptual
              explanations, metaphors, and The Operator's sardonic commentary.
            </p>
            <p className="text-gray-300 leading-relaxed mb-6">
              You'll learn <strong className="text-gray-100">why</strong> things matter, not{' '}
              <strong className="text-gray-100">how</strong> to break them.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-red-500 font-bold mb-3 flex items-center gap-2">
                  <span>❌</span> What You Won't Find Here:
                </h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li>• Step-by-step exploit instructions</li>
                  <li>• Operational command examples</li>
                  <li>• Anything that could be used maliciously</li>
                </ul>
              </div>
              <div>
                <h3 className="text-teal-500 font-bold mb-3 flex items-center gap-2">
                  <span>✅</span> What You Will Find:
                </h3>
                <ul className="text-gray-400 space-y-2 text-sm">
                  <li>• Conceptual frameworks</li>
                  <li>• Design principles</li>
                  <li>• Philosophy and theory</li>
                  <li>• Entertaining explanations</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Featured Tutorial */}
          {tutorials[0].available && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-100 mb-6">Featured Tutorial</h2>
              <Link
                to={`/tutorials/${tutorials[0].slug}`}
                className="block bg-gradient-to-br from-teal-950/50 to-gray-900 border border-teal-500/50 rounded-lg p-8 hover:border-teal-500 transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-teal-500/20 text-teal-400 text-xs font-bold rounded-full uppercase">
                      {tutorials[0].level}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      {tutorials[0].readTime} min read
                    </span>
                  </div>
                  <ArrowRight className="w-6 h-6 text-teal-500 group-hover:translate-x-2 transition-transform" />
                </div>
                <h3 className="text-3xl font-bold text-gray-100 mb-3 group-hover:text-teal-500 transition-colors">
                  {tutorials[0].title}
                </h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  {tutorials[0].excerpt}
                </p>
                <div className="inline-flex items-center gap-2 text-teal-500 font-medium">
                  Read Full Tutorial
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            </div>
          )}

          {/* All Tutorials Grid */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-100 mb-6">All Tutorials</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {tutorials.map((tutorial) => (
                <div
                  key={tutorial.slug}
                  className={`bg-gray-900 border border-gray-800 rounded-lg p-6 ${
                    tutorial.available
                      ? 'hover:border-teal-500 transition-colors'
                      : 'opacity-75'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-bold rounded-full uppercase ${
                        tutorial.available
                          ? 'bg-teal-500/20 text-teal-400'
                          : 'bg-gray-700 text-gray-400'
                      }`}>
                        {tutorial.level}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {tutorial.readTime} min
                      </span>
                    </div>
                    {!tutorial.available && (
                      <span className="text-xs text-gray-500 font-mono">COMING SOON</span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-100 mb-2">
                    {tutorial.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed mb-4 text-sm">
                    {tutorial.excerpt}
                  </p>
                  {tutorial.available ? (
                    <Link
                      to={`/tutorials/${tutorial.slug}`}
                      className="inline-flex items-center gap-2 text-teal-500 font-medium hover:gap-3 transition-all text-sm"
                    >
                      Read Tutorial
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <div className="text-gray-600 text-sm">
                      Check back soon
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Ethics Banner */}
          <div className="ethics-banner">
            <div className="text-center">
              <p className="text-sm text-gray-300 leading-relaxed">
                ⚠️ All tutorials focus on <strong>theory and concepts only</strong>.
                For hands-on security training, visit{' '}
                <a href="https://owasp.org" target="_blank" rel="noopener noreferrer">OWASP</a>,{' '}
                <a href="https://sans.org" target="_blank" rel="noopener noreferrer">SANS</a>,{' '}
                <a href="https://hackthebox.com" target="_blank" rel="noopener noreferrer">HackTheBox</a>, and{' '}
                <a href="https://tryhackme.com" target="_blank" rel="noopener noreferrer">TryHackMe</a>.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TutorialsPage;
